/**
 * Renders the set of actions a user can invoke on a workflow instance.
 *
 * Backed by `useWorkflowVisibleActions(instanceUuid)`. The component is
 * intentionally a flat list of buttons (not a dropdown) so it composes
 * well next to a "current state" header. For domains with many actions,
 * pass `compact` to render an overflow group.
 */

import { useState } from 'react'

import { Button } from '@/shared/components/ui/button'
import { useWorkflowVisibleActions } from '@/shared/hooks/use-workflows'
import { cn } from '@/shared/lib/utils'
import type { WorkflowAction } from '@/shared/lib/workflows/types'

import { WorkflowActionDialog } from './workflow-action-dialog'
import { WorkflowActionIcon } from './action-icon'
import { useWorkflowActionLabel } from './use-workflow-i18n'

export interface WorkflowActionMenuProps {
  instanceUuid: string
  /** Optional cap on the number of buttons shown inline. */
  inlineLimit?: number
  buttonSize?: 'default' | 'xs' | 'sm' | 'lg' | 'icon' | 'icon-xs' | 'icon-sm' | 'icon-lg'
  className?: string
  /** Extra classes applied to every action button. Use for density tweaks
   *  (e.g. wider horizontal padding) when the action buttons sit in a
   *  roomy row and the default button size looks too narrow. */
  buttonClassName?: string
  onActionExecuted?: () => void
  /**
   * Optional workflow key to scope action-label translations to this
   * workflow. Falls back to the global ``workflows.actions.{key}``
   * namespace when omitted.
   */
  workflowKey?: string
  /**
   * Hook for callers that want to take over the click for specific
   * actions (e.g. open a richer "grant exception" modal instead of
   * the generic confirmation dialog). Return ``true`` to suppress
   * the default ``WorkflowActionDialog``; falsy values keep the
   * default behaviour. The handler receives the action plus the
   * instance uuid the menu is bound to.
   */
  onActionClick?: (
    action: WorkflowAction,
    instanceUuid: string,
  ) => boolean | void
}

function variantForUiHint(action: WorkflowAction): 'default' | 'outline' | 'destructive' {
  const hint = action.ui_hints?.['variant']
  if (hint === 'destructive') return 'destructive'
  if (hint === 'primary') return 'default'
  return 'outline'
}

/**
 * Inline trigger so the action label resolves through i18n at the
 * leaf - keeps the parent map free of hook calls and lets each
 * button re-translate independently if locale changes.
 */
function WorkflowActionButton({
  action,
  workflowKey,
  buttonSize,
  buttonClassName,
  onClick,
}: {
  action: WorkflowAction
  workflowKey?: string
  buttonSize?: WorkflowActionMenuProps['buttonSize']
  buttonClassName?: string
  onClick: () => void
}) {
  const label = useWorkflowActionLabel(action.key, action.label, workflowKey)
  const isDestructive = variantForUiHint(action) === 'destructive'
  return (
    <Button
      variant="outline"
      size={buttonSize ?? 'sm'}
      // House action-button style, shared with the claims record's action bar
      // so every engine-driven action surface renders identically: icon +
      // subtle info background + strong border, with a red-tinted variant for
      // destructive actions (deny / cancel / void).
      className={cn(
        'flex cursor-pointer items-center gap-1 rounded-[6px] px-3 py-1.5 text-[11px] transition-all duration-150',
        isDestructive
          ? 'border-[var(--status-error)]/30 bg-[var(--status-error-bg-subtle)] text-[var(--status-error)] hover:border-[var(--status-error)]'
          : 'border-[var(--rule-strong)] bg-[var(--status-info-bg)] text-[var(--ink)] hover:border-[var(--ink-3)]',
        buttonClassName,
      )}
      onClick={onClick}
    >
      <WorkflowActionIcon action={action} />
      {label}
    </Button>
  )
}

export function WorkflowActionMenu({
  instanceUuid,
  inlineLimit = 6,
  buttonSize,
  className,
  buttonClassName,
  onActionExecuted,
  workflowKey,
  onActionClick,
}: WorkflowActionMenuProps) {
  const { data: actions = [], isLoading, error } = useWorkflowVisibleActions(
    instanceUuid,
  )
  const [activeUuid, setActiveUuid] = useState<string | null>(null)
  // Read from the latest listing, so a default read off the record (that
  // can change while the dialog is open) stays current.
  const active = actions.find((action) => action.uuid === activeUuid) ?? null

  if (isLoading) {
    return (
      <div className={cn('flex flex-wrap items-center gap-2', className)}>
        <div className="h-8 w-24 animate-pulse rounded-lg bg-muted" />
        <div className="h-8 w-20 animate-pulse rounded-lg bg-muted" />
      </div>
    )
  }

  if (error || actions.length === 0) {
    return null
  }

  const inline = actions.slice(0, inlineLimit)
  const overflow = actions.slice(inlineLimit)

  return (
    <>
      <div
        className={cn('flex flex-wrap items-center gap-2', className)}
        role="group"
        aria-label="Workflow actions"
      >
        {inline.map((action) => (
          <WorkflowActionButton
            key={action.uuid}
            action={action}
            workflowKey={workflowKey}
            buttonSize={buttonSize}
            buttonClassName={buttonClassName}
            onClick={() => {
              if (onActionClick?.(action, instanceUuid) === true) return
              setActiveUuid(action.uuid)
            }}
          />
        ))}
        {overflow.length > 0 && (
          <span className="text-xs text-muted-foreground">
            +{overflow.length} more
          </span>
        )}
      </div>
      {active && (
        <WorkflowActionDialog
          open={Boolean(active)}
          onOpenChange={(open) => !open && setActiveUuid(null)}
          instanceUuid={instanceUuid}
          action={active}
          workflowKey={workflowKey}
          onSuccess={onActionExecuted}
        />
      )}
    </>
  )
}
